import styled from 'styled-components';
import { motion } from 'framer-motion';

export const Container = styled.div`
  width: 100%;
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const Title = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

export const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormGroup = styled.div`
  position: relative;
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: var(--background-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    background-color: var(--background-secondary);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

export const IconWrapper = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-primary);
    }
  }
`;

export const Button = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

export const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.75rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PasswordRequirements = styled.div`
  background: var(--background-tertiary, var(--background-secondary));
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 0.75rem;
  margin-top: 0.5rem;
`;

export const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  &.valid {
    color: var(--success-color);
  }

  &.invalid {
    color: var(--error-color);
  }

  &.info {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
`;

export const RequirementIcon = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.valid ? 'var(--success-color)' : 'var(--error-color)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 8px;
  font-weight: bold;

  ${props => props.children === '•' && `
    background: var(--text-muted);
    color: var(--background-primary);
    font-size: 10px;
    font-weight: bold;
  `}
`;

export const SuccessMessage = styled(motion.div)`
  background: rgba(56, 161, 105, 0.08);
  border: 1px solid rgba(56, 161, 105, 0.25);
  border-radius: var(--card-radius);
  color: rgba(56, 161, 105, 0.95);
  font-size: 0.875rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

export const BackLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  transition: color 0.2s ease;
  padding: 0.25rem 0;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--primary-dark);
    }
  }
`;

export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
